import clsx from "clsx";
import { MutableRefObject, useEffect, useState } from "react";
// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// MUI
import { Box, Fade, SxProps } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

// Hooks
import useAdvisorPerformanceTableConfig from "@DataPages/components/ServiceOperations/Tabs/AdvisorPerformance/hooks/useAdvisorPerformanceTableConfig";

// Helpers
import { generateSx } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import { tableKeys } from "@/Constants/tableKeys";

// Types
import { IAdvisorPerformance } from "@/DataPages/types/advisorPerformance/model";
import { IAdvisorPerformanceFooterModel, IAggregations } from "@DataPages/components/ServiceOperations/Tabs/AdvisorPerformance/interfaces";

const resolveNoRowsOverlay = (title: string) => {
	if (title === "Advisor Performance") {
		return "No Advisor Performance Data";
	} else {
		return "No Performance Data";
	}
};

const AUTO_GENERATED_ID = "auto-generated-group-footer-root";

interface IAdvisorPerformanceDataTableProps {
	apiRef: MutableRefObject<GridApiPremium>;
	title: string;
	rows: IAdvisorPerformance[] | null;
	footerModels: IAdvisorPerformanceFooterModel;
	aggregations: IAggregations;
	hasNextPage: boolean;
}

// Constant configurations
const pinnedColumns = {
	left: ["_id", "userFullName", "department", "repairOrderNumber", "date", "repairOrderCount", "__empty"],
};

const flattenFooterModels = <T extends Record<string, unknown>>(footerModels: T): Record<string, string> => {
	const result: Record<string, string> = {};

	const flatten = (obj: Record<string, unknown>, parentKey = "") => {
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				const newKey = parentKey ? `${parentKey}.${key}` : key;
				if (typeof obj[key] === "object" && obj[key] !== null) {
					flatten(obj[key] as Record<string, unknown>, newKey);
				} else {
					result[newKey] = String(obj[key]);
				}
			}
		}
	};

	flatten(footerModels);
	return result;
};

export default function AdvisorPerformanceDataTable(props: Readonly<IAdvisorPerformanceDataTableProps>) {
	const { apiRef, title, rows, footerModels, aggregations, hasNextPage } = props;

	const { columns, groupingColDef } = useAdvisorPerformanceTableConfig(apiRef, hasNextPage);
	const showTableConditions = [Boolean(columns?.length), Boolean(apiRef)];

	const footerModelsFlat = flattenFooterModels(footerModels);

	const [sx, setSx] = useState<SxProps>({});
	useEffect(() => {
		setSx(generateSx(apiRef, { yGap: "0px" }));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [apiRef]);

	if (showTableConditions.some((condition) => !condition)) {
		return null;
	}

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.ADVISOR_PERFORMANCE,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={rows ?? []}
			maxRows={15}
			pinnedColumns={pinnedColumns}
			enableCustomScrollbars
			hideFooter
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			initialState={{
				aggregation: {
					model: footerModelsFlat,
				},
			}}
			slots={{
				noRowsOverlay: NoRowsOverlay,
				toolbar: CommonToolbar,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "mt-[5px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					customComponent: (
						<>
							{hasNextPage && (
								<Fade in={hasNextPage} timeout={400}>
									<Box className="my-[16px]">
										<LoadingSpinner label="Advisor data is still loading..." className="items-center" />
									</Box>
								</Fade>
							)}
						</>
					),
					filter: {
						hide: false,
					},
					search: {
						useQuickFilter: true,
					},
					expand: {
						hide: true,
					},
				},
				noRowsOverlay: {
					title: resolveNoRowsOverlay(title),
				},
			}}
			getRowClassName={(params) => {
				return clsx({
					"group/footer table-footer footer": params.id === AUTO_GENERATED_ID,
					"group/stripe even-row": params.indexRelativeToCurrentPage % 2 === 1,
					"group/stripe odd-row": params.indexRelativeToCurrentPage % 2 === 0,
				});
			}}
			customProps={{
				tableContainerClassName:
					"[&_.MuiDataGrid-main]:mt-2 [&_.MuiDataGrid-cellEmpty]:border-none after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
				tableClassName: {
					"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov":
						true,
				},
				customAggregationsFn: aggregations,
			}}
			sx={sx}
			groupingColDef={groupingColDef}
		/>
	);
}
